<template>
  <div :class="ns.b()" @click="handleClick">
    <z-input :id="id" ref="input" :model-value="displayValue" :placeholder="placeholder" :readonly="true"
      :disabled="inputTableDisabled" :size="inputTableSize" :name="name" :validate-event="false"
      :clearable="inputClearable" @clear="handleClear" @input="handleInput" @change="handleInputChange"
      @focus="handleFocus" @blur="handleBlur">
      <template #append>
        <span :class="ns.e('icon-trigger')" style="display: inline-flex; cursor: pointer" @click.stop="handleClick">
          <z-icon>
            <Search />
          </z-icon>
        </span>
      </template>
    </z-input>

    <z-dialog v-model="dialogVisible" align-center append-to-body width="72%">
      <z-grid
        v-if="dialogVisible"
        ref="gridRef"
        :options="gridOptions"
        :auto-height="true"
      />

      <template #footer>
        <div class="dialog-footer">
          <z-button @click="dialogVisible = false">取 消</z-button>
          <z-button type="primary" @click="handleConfirm">确 认</z-button>
        </div>
      </template>
    </z-dialog>
  </div>
</template>

<script lang="ts" setup>
import { computed, nextTick, reactive, ref, watch } from 'vue'
import { isNil } from 'lodash-unified'
import ZInput from '@zoryn/components/input'
import ZIcon from '@zoryn/components/icon'
import ZDialog from '@zoryn/components/dialog'
import ZButton from '@zoryn/components/button'
import ZGrid from '@zoryn/components/grid'
import { useGlobalConfig } from '@zoryn/components/config-provider'
import {
  useFormDisabled,
  useFormItem,
  useFormSize,
} from '@zoryn/components/form'
import { useNamespace, resolveFetchLovList, getLovLabel } from '@zoryn/hooks'
import { NOOP, isNumber } from '@zoryn/utils'
import { Search } from '@zoryn/icons'
import {
  CHANGE_EVENT,
  INPUT_EVENT,
  UPDATE_MODEL_EVENT,
} from '@zoryn/constants'
import {
  clearInputTableMappedSource,
  inputTableEmits,
  inputTableProps,
  resolveInputTableGridOptions,
  resolveInputTableValueField,
  resolveInputTableValueSourceKey,
} from './input-table'

import type { InputInstance } from '@zoryn/components/input'

defineOptions({
  name: 'ZInputTable',
})

interface GridExpose {
  getCurrentRow: () => Record<string, unknown> | null
  loadTableData: (datas: Record<string, unknown>[]) => Promise<void>
}

const props = defineProps(inputTableProps)
const emit = defineEmits(inputTableEmits)

const ns = useNamespace('input-table')
const input = ref<InputInstance>()
const gridRef = ref<GridExpose>()
const dialogVisible = ref(false)
const opening = ref(false)

const data = reactive<{ userInput: string | null }>({
  userInput: null,
})

const { formItem } = useFormItem()
const inputTableSize = useFormSize()
const inputTableDisabled = useFormDisabled()
const lovConfig = useGlobalConfig('lov')

const inputClearable = computed(
  () => props.clearable && !inputTableDisabled.value && !props.readonly
)

const gridOptions = computed(() => resolveInputTableGridOptions(props.options))

/** 展示：优先 displayText；v-model 绑 valueField 列时展示该值；否则 map 非 valueField 字段；LOV / modelValue 回退 */
const displayValue = computed(() => {
  if (data.userInput !== null) {
    return data.userInput
  }
  if (props.displayText !== undefined) {
    return props.displayText
  }
  const valueField = resolveInputTableValueField(props.options, props.map)
  const lovType = props.options?.lovType
  if (lovType && !isNil(props.modelValue)) {
    const label = getLovLabel(lovType, props.modelValue)
    if (label) {
      return label
    }
  }
  const valueSourceKey = resolveInputTableValueSourceKey(props.map ?? {}, valueField)
  if (valueSourceKey) {
    const val = props.modelValue ?? props.source?.[valueSourceKey]
    if (!isNil(val) && val !== '') {
      return String(val)
    }
  }
  if (props.source && props.map) {
    for (const key in props.map) {
      if (props.map[key] === valueField) {
        continue
      }
      const text = props.source[key]
      if (text !== null && text !== undefined && text !== '') {
        return String(text)
      }
    }
  }
  if (isNil(props.modelValue)) {
    return ''
  }
  return String(props.modelValue)
})

const parseModelValue = (value: string): string | number | null => {
  if (value === '') {
    return null
  }
  const num = Number.parseFloat(value)
  return Number.isNaN(num) ? value : num
}

const setModelValue = (newVal: string | number | null) => {
  const oldVal = props.modelValue ?? null
  if (oldVal === newVal) {
    return
  }
  emit(UPDATE_MODEL_EVENT, newVal ?? undefined)
  emit(CHANGE_EVENT, newVal ?? undefined, oldVal ?? undefined)
  if (props.validateEvent) {
    formItem?.validate?.('change').catch(NOOP)
  }
}

const runBeforeOpen = async (): Promise<boolean> => {
  const hook = props.beforeOpen ?? props.options?.beforeOpen
  if (!hook) {
    return true
  }
  const result = await hook(props.source)
  return result !== false
}

const handleClick = async () => {
  if (inputTableDisabled.value || props.readonly || opening.value) {
    return
  }
  opening.value = true
  try {
    const pass = await runBeforeOpen()
    if (!pass) {
      return
    }
    dialogVisible.value = true
  } finally {
    opening.value = false
  }
}

const loadDialogGridData = async () => {
  await nextTick()
  await nextTick()
  const lovType = props.options?.lovType
  if (lovType) {
    try {
      const fetchList = resolveFetchLovList(lovConfig.value)
      const rows = await fetchList(lovType)
      await gridRef.value?.loadTableData?.(rows)
    } catch {
      await gridRef.value?.loadTableData?.([])
    }
    return
  }
  const rows = props.options?.data
  if (rows?.length) {
    await gridRef.value?.loadTableData?.(rows)
  }
}

watch(dialogVisible, (visible) => {
  if (visible) {
    void loadDialogGridData()
  }
})

const resolveRowModelValue = (
  row: Record<string, unknown>
): string | number | null => {
  const field = resolveInputTableValueField(props.options, props.map)
  const raw = row[field]
  if (raw === null || raw === undefined || raw === '') {
    return null
  }
  if (typeof raw === 'string' || isNumber(raw)) {
    return raw
  }
  const num = Number(raw)
  return Number.isNaN(num) ? String(raw) : num
}

const clearSelection = () => {
  clearInputTableMappedSource(props.source, props.map)
  data.userInput = null
  setModelValue(null)
}

const handleClear = () => {
  clearSelection()
  emit('clear')
}

const handleConfirm = () => {
  const row = gridRef.value?.getCurrentRow?.()
  if (!row) {
    return
  }
  if (props.source && props.map) {
    for (const key in props.map) {
      props.source[key] = row[props.map[key]]
    }
  }
  setModelValue(resolveRowModelValue(row))
  dialogVisible.value = false
}

const handleInput = (value: string) => {
  data.userInput = value
  emit(INPUT_EVENT, parseModelValue(value))
  emit(UPDATE_MODEL_EVENT, parseModelValue(value) ?? undefined)
}

const handleInputChange = (value: string) => {
  data.userInput = null
  if (value === '') {
    clearSelection()
    return
  }
  setModelValue(parseModelValue(value))
}

const focus = () => {
  input.value?.focus?.()
}

const blur = () => {
  input.value?.blur?.()
}

const handleFocus = (event: MouseEvent | FocusEvent) => {
  emit('focus', event as FocusEvent)
}

const handleBlur = (event: MouseEvent | FocusEvent) => {
  data.userInput = null
  emit('blur', event as FocusEvent)
  if (props.validateEvent) {
    formItem?.validate?.('blur').catch(NOOP)
  }
}

defineExpose({
  focus,
  blur,
  clear: handleClear,
})
</script>
